import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit3, FolderOpen, Loader2, Plus, Tag, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { MAX_AGENT_CATEGORY_LENGTH } from '@/constants/agentCategories';
import {
  createAgentCategory,
  deleteAgentCategory,
  fetchAgentCategoryCatalogue,
  renameAgentCategory,
  type AgentCategoryRecord,
} from '@/lib/agentCategories';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface AgentCategoryManagerProps {
  canCreate: boolean;
  canAdminister: boolean;
  onCategoriesChanged: () => void;
}

interface CategoryFormState {
  id: string | null;
  name: string;
  description: string;
}

const EMPTY_FORM: CategoryFormState = { id: null, name: '', description: '' };

export function AgentCategoryManager({
  canCreate,
  canAdminister,
  onCategoriesChanged,
}: AgentCategoryManagerProps) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CategoryFormState>(EMPTY_FORM);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AgentCategoryRecord | null>(null);

  const catalogueQuery = useQuery({
    queryKey: ['agent-category-catalogue'],
    queryFn: fetchAgentCategoryCatalogue,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['agent-category-catalogue'] });
    onCategoriesChanged();
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const name = form.name.trim();
      if (!name) throw new Error('Enter a category name.');
      return form.id
        ? renameAgentCategory(form.id, name, form.description.trim())
        : createAgentCategory(name, form.description.trim());
    },
    onSuccess: async (category) => {
      toast.success(form.id ? `${category.name} updated` : `${category.name} created`);
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      await refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (categoryId: string) => deleteAgentCategory(categoryId),
    onSuccess: async () => {
      toast.success('Category deleted');
      setDeleteTarget(null);
      await refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (category: AgentCategoryRecord) => {
    setForm({
      id: category.id,
      name: category.name,
      description: category.description || '',
    });
    setDialogOpen(true);
  };

  if (catalogueQuery.isLoading) {
    return <div className="flex min-h-56 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  if (catalogueQuery.error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Categories unavailable</AlertTitle>
        <AlertDescription>{(catalogueQuery.error as Error).message}</AlertDescription>
      </Alert>
    );
  }

  const catalogue = catalogueQuery.data;
  const categories = catalogue?.categories || [];

  return (
    <section className="overflow-hidden rounded-md border bg-card" aria-labelledby="agent-categories-title">
      <div className="flex flex-col gap-4 border-b px-5 py-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 id="agent-categories-title" className="text-xl font-semibold">Agent categories</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Create reusable categories for organising agents and learner simulations. Renaming a category updates every assigned agent.
          </p>
        </div>
        {canCreate && catalogue?.managed && (
          <Button type="button" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" /> Add category
          </Button>
        )}
      </div>

      {canCreate && !canAdminister && catalogue?.managed && (
        <Alert className="m-5 mb-0">
          <FolderOpen className="h-4 w-4" />
          <AlertTitle>Category contributor access</AlertTitle>
          <AlertDescription>
            Unit Managers and Unit Administrators can add categories for agents in their authorised units. Categories are company-wide, so only Company and Branch Administrators can rename or delete them.
          </AlertDescription>
        </Alert>
      )}

      {!catalogue?.managed && (
        <Alert className="m-5">
          <FolderOpen className="h-4 w-4" />
          <AlertTitle>Legacy category mode</AlertTitle>
          <AlertDescription>Categories are currently created from an agent form. Apply the managed catalogue migration to enable central administration.</AlertDescription>
        </Alert>
      )}

      {categories.length ? (
        <div className="divide-y">
          {categories.map((category) => (
            <div key={category.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Tag className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-foreground">{category.name}</p>
                    <Badge variant="secondary">{category.usageCount} agent{category.usageCount === 1 ? '' : 's'}</Badge>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {category.description || 'No description has been added.'}
                  </p>
                </div>
              </div>
              {canAdminister && catalogue.managed && (
                <div className="flex shrink-0 items-center gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => openEdit(category)}>
                    <Edit3 className="mr-2 h-4 w-4" /> Edit
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 text-destructive hover:text-destructive"
                    disabled={category.usageCount > 0}
                    onClick={() => setDeleteTarget(category)}
                    aria-label={`Delete ${category.name}`}
                    title={category.usageCount > 0 ? 'Reassign the category’s agents before deleting it.' : `Delete ${category.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex min-h-56 flex-col items-center justify-center px-6 text-center">
          <Tag className="mb-3 h-8 w-8 text-muted-foreground" />
          <p className="font-medium">No categories yet</p>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">Add the first category, then select it when creating or editing an agent.</p>
          {canCreate && catalogue?.managed && <Button type="button" className="mt-4" onClick={openCreate}><Plus className="mr-2 h-4 w-4" />Add category</Button>}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) setForm(EMPTY_FORM);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit category' : 'Add category'}</DialogTitle>
            <DialogDescription>
              {form.id ? 'Renaming this category updates every agent currently assigned to it.' : 'The new category becomes available in all agent forms and filters.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="agent-category-name">Category name</Label>
              <Input
                id="agent-category-name"
                value={form.name}
                maxLength={MAX_AGENT_CATEGORY_LENGTH}
                onChange={(event) => setForm((previous) => ({ ...previous, name: event.target.value }))}
                placeholder="e.g. Clinical, Sales or Leadership"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">Up to {MAX_AGENT_CATEGORY_LENGTH} characters.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="agent-category-description">Description <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Textarea
                id="agent-category-description"
                value={form.description}
                onChange={(event) => setForm((previous) => ({ ...previous, description: event.target.value }))}
                placeholder="Explain when administrators should use this category."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button type="button" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.name.trim()}>
              {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {form.id ? 'Save changes' : 'Create category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the category from the catalogue. Categories assigned to agents must be reassigned before deletion.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete category
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
